/**
 * Tab Bar Component
 * Navigation between different viewer tabs
 */

import React from 'react';
import { useApp } from '../../store/AppContext';
import { TABS, getEnabledTabs } from '../../../shared/types/project';
import './TabBar.css';

// Tab icons as SVG components
export const TabIcons: Record<string, React.ReactNode> = {
  tour: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  ),
  cube: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <path d="M3.27 6.96L12 12.01l8.73-5.05" />
      <path d="M12 22.08V12" />
    </svg>
  ),
  scatter: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="7" cy="7" r="2" />
      <circle cx="17" cy="7" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="7" cy="17" r="2" />
      <circle cx="17" cy="17" r="2" />
      <circle cx="5" cy="12" r="1.5" />
      <circle cx="19" cy="12" r="1.5" />
    </svg>
  ),
  map: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M1 6v16l7-4 8 4 7-4V2l-7 4-8-4-7 4z" />
      <path d="M8 2v16" />
      <path d="M16 6v16" />
    </svg>
  ),
  temperature: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z" />
    </svg>
  ),
};

export function TabBar() {
  const { state, setActiveTab, togglePdfPanel, toggleDualView, dispatch } = useApp();

  // Get visible tabs based on project configuration
  const enabledTabIds = getEnabledTabs(state.currentProject);
  const visibleTabs = TABS.filter(tab => enabledTabIds.includes(tab.id));

  // Handle tab click - exit split view if active and switch to tab
  const handleTabClick = (tabId: typeof state.activeTab) => {
    if (state.dualViewMode) {
      // Exit split view mode when a tab is clicked
      dispatch({ type: 'TOGGLE_DUAL_VIEW' });
    }
    setActiveTab(tabId);
  };

  return (
    <nav className="tabbar">
      <div className="tabbar__tabs">
        {visibleTabs.map(tab => (
          <button
            key={tab.id}
            className={`tabbar__tab ${state.activeTab === tab.id && !state.dualViewMode ? 'tabbar__tab--active' : ''}`}
            onClick={() => handleTabClick(tab.id)}
            title={`${tab.label} (${tab.shortcut})`}
          >
            <span className="tabbar__tab-icon">
              {TabIcons[tab.icon]}
            </span>
            <span className="tabbar__tab-label">{tab.label}</span>
          </button>
        ))}
      </div>

      <div className="tabbar__actions">
        <button
          className={`tabbar__action ${state.dualViewMode ? 'tabbar__action--active' : ''}`}
          onClick={toggleDualView}
          title="Split View - Compare two viewers side by side"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="7" height="18" rx="1" />
            <rect x="14" y="3" width="7" height="18" rx="1" />
          </svg>
          <span>Split View</span>
        </button>
        <button
          className={`tabbar__action ${state.pdfPanelOpen ? 'tabbar__action--active' : ''}`}
          onClick={togglePdfPanel}
          title="Toggle PDF Panel (Ctrl+P)"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <path d="M14 2v6h6" />
            <path d="M16 13H8" />
            <path d="M16 17H8" />
            <path d="M10 9H8" />
          </svg>
          <span>PDF</span>
        </button>
      </div>
    </nav>
  );
}
